import { getEffectiveHeartRateData } from '../../../utils/hrDataRepair';
import {
  ARTIFACT_METHOD,
  DETREND_METHOD,
  NN_SOURCE,
  PREPROCESSING_VERSION,
  RESAMPLE_HZ,
  RMSSD_SOURCE,
  SMOOTH_PRIORS_LAMBDA,
  SPECTRAL_PATH_DEVICE,
  SPECTRAL_PATH_PRIMARY,
  preprocessHrvPath,
} from '../shared/hrv-preprocessing';
import { DEFAULT_WELCH_SAMPLE_RATE_HZ } from '../shared/spectral/constants';
import { computeWelchPsd } from '../shared/spectral/computeWelchPsd';
import type {
  AlignedFeaturePoint,
  ExcludedSessionReason,
  FeatureKey,
  FeatureSample,
  GapInfo,
  PsdSeries,
  SpectralAnalysisSnapshot,
  SpectralBandMetrics,
  SpectralGroupSummary,
  SpectralSessionResult,
  StatisticalValidationEntry,
  ValidationInputSession,
  ValidatedSessionAnalysis,
} from '../types';

const FEATURE_KEYS: FeatureKey[] = ['HR', 'HRV', 'LF', 'HF'];
const MIN_VALID_PERCENT = 80;
const SEGMENT_DURATION_SECONDS = 300;
const FULL_SESSION_DURATION_SECONDS = 600;
const QUALITY_WARNING_THRESHOLD = 90;
const SMALL_GAP_SECONDS = 3;
const SAMPLING_RATE = DEFAULT_WELCH_SAMPLE_RATE_HZ;
const LOW_PASS_CUTOFF_HZ = 0.5;

interface ParsedSession {
  sessionId: number;
  patientId: string;
  sessionDate: string;
  samples: Record<FeatureKey, FeatureSample[]>;
  rawCounts: Record<FeatureKey, number>;
}

interface ValidationSuccess {
  included: true;
  validation: ValidatedSessionAnalysis;
}

interface ValidationFailure {
  included: false;
  excluded: ExcludedSessionReason;
}

type ValidationResult = ValidationSuccess | ValidationFailure;

const clampNumber = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const round = (value: number, digits = 4) => {
  if (!Number.isFinite(value)) return 0;
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
};

const erf = (value: number) => {
  const sign = value < 0 ? -1 : 1;
  const absolute = Math.abs(value);
  const a1 = 0.254829592;
  const a2 = -0.284496736;
  const a3 = 1.421413741;
  const a4 = -1.453152027;
  const a5 = 1.061405429;
  const p = 0.3275911;
  const t = 1 / (1 + p * absolute);
  const y =
    1 -
    ((((a5 * t + a4) * t + a3) * t + a2) * t + a1) *
      t *
      Math.exp(-absolute * absolute);
  return sign * y;
};

const normalCdf = (value: number) => 0.5 * (1 + erf(value / Math.SQRT2));

const inverseNormalCdf = (probability: number) => {
  const p = clampNumber(probability, 1e-12, 1 - 1e-12);
  const a = [
    -3.969683028665376e1, 2.209460984245205e2, -2.759285104469687e2,
    1.38357751867269e2, -3.066479806614716e1, 2.506628277459239,
  ];
  const b = [
    -5.447609879822406e1, 1.615858368580409e2, -1.556989798598866e2,
    6.680131188771972e1, -1.328068155288572e1,
  ];
  const c = [
    -7.784894002430293e-3, -3.223964580411365e-1, -2.400758277161838,
    -2.549732539343734, 4.374664141464968, 2.938163982698783,
  ];
  const d = [
    7.784695709041462e-3, 3.224671290700398e-1, 2.445134137142996,
    3.754408661907416,
  ];

  const lower = 0.02425;
  const upper = 1 - lower;

  if (p < lower) {
    const q = Math.sqrt(-2 * Math.log(p));
    return (
      (((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) /
      ((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1)
    );
  }

  if (p > upper) {
    const q = Math.sqrt(-2 * Math.log(1 - p));
    return -(
      (((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) /
      ((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1)
    );
  }

  const q = p - 0.5;
  const r = q * q;
  return (
    ((((((a[0] * r + a[1]) * r + a[2]) * r + a[3]) * r + a[4]) * r + a[5]) *
      q) /
    (((((b[0] * r + b[1]) * r + b[2]) * r + b[3]) * r + b[4]) * r + 1)
  );
};

const logGamma = (value: number): number => {
  const coefficients = [
    676.5203681218851, -1259.1392167224028, 771.3234287776531,
    -176.6150291621406, 12.507343278686905, -0.13857109526572012,
    9.984369578019572e-6, 1.5056327351493116e-7,
  ];

  if (value < 0.5) {
    return (
      Math.log(Math.PI) -
      Math.log(Math.sin(Math.PI * value)) -
      logGamma(1 - value)
    );
  }

  let x = 0.9999999999998099;
  const adjusted = value - 1;

  coefficients.forEach((coefficient, index) => {
    x += coefficient / (adjusted + index + 1);
  });

  const t = adjusted + coefficients.length - 0.5;
  return (
    0.5 * Math.log(2 * Math.PI) +
    (adjusted + 0.5) * Math.log(t) -
    t +
    Math.log(x)
  );
};

const regularizedIncompleteBeta = (x: number, a: number, b: number): number => {
  const clampedX = clampNumber(x, 0, 1);
  if (clampedX === 0 || clampedX === 1) return clampedX;

  const continuedFraction = (
    xValue: number,
    aValue: number,
    bValue: number
  ): number => {
    const maxIterations = 200;
    const epsilon = 3e-7;
    const fpmin = 1e-30;
    const qab = aValue + bValue;
    const qap = aValue + 1;
    const qam = aValue - 1;
    let c = 1;
    let d = 1 - (qab * xValue) / qap;
    if (Math.abs(d) < fpmin) d = fpmin;
    d = 1 / d;
    let h = d;

    for (let iteration = 1; iteration <= maxIterations; iteration += 1) {
      const m = iteration;
      const m2 = 2 * m;
      let aa = (m * (bValue - m) * xValue) / ((qam + m2) * (aValue + m2));
      d = 1 + aa * d;
      if (Math.abs(d) < fpmin) d = fpmin;
      c = 1 + aa / c;
      if (Math.abs(c) < fpmin) c = fpmin;
      d = 1 / d;
      h *= d * c;

      aa = (-(aValue + m) * (qab + m) * xValue) / ((aValue + m2) * (qap + m2));
      d = 1 + aa * d;
      if (Math.abs(d) < fpmin) d = fpmin;
      c = 1 + aa / c;
      if (Math.abs(c) < fpmin) c = fpmin;
      d = 1 / d;
      const delta = d * c;
      h *= delta;

      if (Math.abs(delta - 1) < epsilon) break;
    }

    return h;
  };

  const logBeta =
    logGamma(a + b) -
    logGamma(a) -
    logGamma(b) +
    a * Math.log(clampedX) +
    b * Math.log(1 - clampedX);
  const front = Math.exp(logBeta);

  if (clampedX < (a + 1) / (a + b + 2)) {
    return (front * continuedFraction(clampedX, a, b)) / a;
  }

  return 1 - (front * continuedFraction(1 - clampedX, b, a)) / b;
};

const studentTCdf = (value: number, degreesOfFreedom: number) => {
  if (degreesOfFreedom <= 0) return value < 0 ? 0 : 1;
  if (value === 0) return 0.5;
  const x = degreesOfFreedom / (degreesOfFreedom + value * value);
  const ib = regularizedIncompleteBeta(x, degreesOfFreedom / 2, 0.5);
  return value > 0 ? 1 - 0.5 * ib : 0.5 * ib;
};

const inverseStudentT = (probability: number, degreesOfFreedom: number) => {
  const target = clampNumber(probability, 1e-8, 1 - 1e-8);
  let lower = -20;
  let upper = 20;

  for (let iteration = 0; iteration < 120; iteration += 1) {
    const mid = (lower + upper) / 2;
    const cdf = studentTCdf(mid, degreesOfFreedom);
    if (cdf < target) {
      lower = mid;
    } else {
      upper = mid;
    }
  }

  return (lower + upper) / 2;
};

const effectSizeLabel = (
  value: number
): 'negligible' | 'small' | 'medium' | 'large' => {
  const absolute = Math.abs(value);
  if (absolute < 0.2) return 'negligible';
  if (absolute < 0.5) return 'small';
  if (absolute < 0.8) return 'medium';
  return 'large';
};

const average = (values: number[]) => {
  if (values.length === 0) return 0;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
};

const standardDeviation = (values: number[]) => {
  if (values.length <= 1) return 0;
  const mean = average(values);
  const variance =
    values.reduce((sum, value) => sum + (value - mean) ** 2, 0) /
    (values.length - 1);
  return Math.sqrt(Math.max(variance, 0));
};

const minValue = (values: number[]) =>
  values.length ? Math.min(...values) : 0;
const maxValue = (values: number[]) =>
  values.length ? Math.max(...values) : 0;

const linearTrend = (values: number[]) => {
  if (values.length <= 1) return 0;
  const xMean = (values.length - 1) / 2;
  const yMean = average(values);
  let numerator = 0;
  let denominator = 0;

  for (let index = 0; index < values.length; index += 1) {
    const xDiff = index - xMean;
    numerator += xDiff * (values[index] - yMean);
    denominator += xDiff ** 2;
  }

  if (denominator === 0) return 0;
  return numerator / denominator;
};

const computeRmssd = (values: number[]) => {
  if (values.length <= 1) return undefined;
  const diffs: number[] = [];
  for (let index = 1; index < values.length; index += 1) {
    diffs.push(values[index] - values[index - 1]);
  }
  return Math.sqrt(average(diffs.map(diff => diff ** 2)));
};

const toIso = (timestamp: number) => new Date(timestamp).toISOString();

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const eventTimestamp = (value: unknown) => {
  if (typeof value === 'number') return Math.floor(value / 1000) * 1000;
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : Math.floor(parsed / 1000) * 1000;
  }
  if (value instanceof Date) return Math.floor(value.getTime() / 1000) * 1000;
  return null;
};

const parseSession = (session: ValidationInputSession): ParsedSession => {
  const events = getEffectiveHeartRateData({
    hrData: session.hrData ?? null,
    hrData_repaired: session.hrData_repaired ?? null,
  } as ValidationInputSession);
  const samples: Record<FeatureKey, FeatureSample[]> = {
    HR: [],
    HRV: [],
    LF: [],
    HF: [],
  };
  const rawCounts: Record<FeatureKey, number> = {
    HR: 0,
    HRV: 0,
    LF: 0,
    HF: 0,
  };

  events.forEach(event => {
    const timestamp = eventTimestamp(event.timestamp);
    if (timestamp == null) return;

    const featureValues: Record<FeatureKey, unknown> = {
      HR: event.hr,
      HRV: event.hrv,
      LF: event.lf,
      HF: event.hf,
    };

    FEATURE_KEYS.forEach(feature => {
      const value = featureValues[feature];
      rawCounts[feature] += 1;
      if (isFiniteNumber(value)) {
        samples[feature].push({ timestamp, value });
      }
    });
  });

  return {
    sessionId: session.id,
    patientId: session.patientCode || `SESSION-${session.id}`,
    sessionDate:
      session.started_at ||
      session.created_at ||
      session.updated_at ||
      new Date().toISOString(),
    samples,
    rawCounts,
  };
};

const mapByTimestamp = (samples: FeatureSample[]) => {
  const timestampMap = new Map<number, number[]>();

  samples.forEach(sample => {
    const current = timestampMap.get(sample.timestamp) ?? [];
    current.push(sample.value);
    timestampMap.set(sample.timestamp, current);
  });

  return timestampMap;
};

const getIntersectedTimestamps = (
  maps: Record<FeatureKey, Map<number, number[]>>
): number[] => {
  const [first, ...rest] = FEATURE_KEYS;
  const timestamps = Array.from(maps[first].keys()).filter(timestamp =>
    rest.every(feature => maps[feature].has(timestamp))
  );

  return timestamps.sort((left, right) => left - right);
};

const buildAlignedSeries = (
  samples: Record<FeatureKey, FeatureSample[]>,
  cleaningLog: string[]
) => {
  const sampleMaps: Record<FeatureKey, Map<number, number[]>> = {
    HR: mapByTimestamp(samples.HR),
    HRV: mapByTimestamp(samples.HRV),
    LF: mapByTimestamp(samples.LF),
    HF: mapByTimestamp(samples.HF),
  };

  const commonTimestamps = getIntersectedTimestamps(sampleMaps);
  cleaningLog.push(
    `Aligned 4 series to ${commonTimestamps.length} common timestamps`
  );

  const duplicatesByFeature: Record<FeatureKey, number> = {
    HR: 0,
    HRV: 0,
    LF: 0,
    HF: 0,
  };

  const aligned = commonTimestamps.map<AlignedFeaturePoint>(timestamp => {
    const point = { timestamp, HR: 0, HRV: 0, LF: 0, HF: 0 };

    FEATURE_KEYS.forEach(feature => {
      const values = sampleMaps[feature].get(timestamp) ?? [];
      duplicatesByFeature[feature] += Math.max(0, values.length - 1);
      point[feature] = average(values);
    });

    return point;
  });

  const duplicatesRemoved = FEATURE_KEYS.reduce(
    (sum, feature) => sum + duplicatesByFeature[feature],
    0
  );
  cleaningLog.push(
    `Deduplication: HR ${duplicatesByFeature.HR} removed, HRV ${duplicatesByFeature.HRV} removed, LF ${duplicatesByFeature.LF} removed, HF ${duplicatesByFeature.HF} removed`
  );

  return {
    aligned,
    duplicatesRemoved,
  };
};

const interpolateValue = (
  startValue: number,
  endValue: number,
  stepIndex: number,
  totalSteps: number
) => startValue + ((endValue - startValue) * stepIndex) / totalSteps;

const detectAndHandleGaps = (
  aligned: AlignedFeaturePoint[],
  cleaningLog: string[]
) => {
  if (aligned.length === 0) {
    return {
      points: aligned,
      gaps: [] as GapInfo[],
      interpolatedPoints: 0,
    };
  }

  const withGapsHandled: AlignedFeaturePoint[] = [aligned[0]];
  const gaps: GapInfo[] = [];
  let interpolatedPoints = 0;
  let interpolatedGapCount = 0;
  let excludedGapCount = 0;

  for (let index = 0; index < aligned.length - 1; index += 1) {
    const current = aligned[index];
    const next = aligned[index + 1];
    const deltaSec = Math.round((next.timestamp - current.timestamp) / 1000);

    if (deltaSec > 1) {
      const gapInfo: GapInfo = {
        startTime: toIso(current.timestamp),
        durationSec: deltaSec - 1,
        featureAffected: 'HR, HRV, LF, HF',
        interpolated: deltaSec <= SMALL_GAP_SECONDS,
      };
      gaps.push(gapInfo);

      if (deltaSec <= SMALL_GAP_SECONDS) {
        interpolatedGapCount += 1;
        for (let step = 1; step < deltaSec; step += 1) {
          withGapsHandled.push({
            timestamp: current.timestamp + step * 1000,
            HR: interpolateValue(current.HR, next.HR, step, deltaSec),
            HRV: interpolateValue(current.HRV, next.HRV, step, deltaSec),
            LF: interpolateValue(current.LF, next.LF, step, deltaSec),
            HF: interpolateValue(current.HF, next.HF, step, deltaSec),
          });
          interpolatedPoints += 1;
        }
        cleaningLog.push(
          `Gap detected ${toIso(current.timestamp)}-${toIso(next.timestamp)} (${deltaSec - 1}sec) on HR, HRV, LF, HF -> interpolated`
        );
      } else {
        excludedGapCount += 1;
        cleaningLog.push(
          `Gap detected ${toIso(current.timestamp)}-${toIso(next.timestamp)} (${deltaSec - 1}sec) on HR, HRV, LF, HF -> marked as risk`
        );
      }
    }

    withGapsHandled.push(next);
  }

  cleaningLog.push(
    `Gaps: ${gaps.length} total, interpolated: ${interpolatedGapCount}, excluded: ${excludedGapCount}`
  );

  return {
    points: withGapsHandled.sort(
      (left, right) => left.timestamp - right.timestamp
    ),
    gaps,
    interpolatedPoints,
  };
};

const buildSegmentSeries = (
  points: AlignedFeaturePoint[],
  startTimestamp: number,
  endTimestamp: number
) => {
  const slotCount = Math.round((endTimestamp - startTimestamp) / 1000) + 1;
  const byTimestamp = new Map(
    points
      .filter(
        point =>
          point.timestamp >= startTimestamp && point.timestamp <= endTimestamp
      )
      .map(point => [point.timestamp, point] as const)
  );

  const timestamps = Array.from(
    { length: slotCount },
    (_, index) => startTimestamp + index * 1000
  );

  const valuesForFeature = (feature: FeatureKey) =>
    timestamps.map(timestamp => {
      const point = byTimestamp.get(timestamp);
      if (!point) return undefined;
      const value = point[feature];
      return isFiniteNumber(value) ? value : undefined;
    });

  return {
    timestamps,
    values: FEATURE_KEYS.reduce(
      (accumulator, feature) => ({
        ...accumulator,
        [feature]: valuesForFeature(feature),
      }),
      {} as Record<FeatureKey, Array<number | undefined>>
    ),
  };
};

const countFilledSegmentSeconds = (
  series: ReturnType<typeof buildSegmentSeries>
) =>
  Math.min(
    ...FEATURE_KEYS.map(
      feature =>
        series.values[feature].filter((value): value is number =>
          isFiniteNumber(value)
        ).length
    )
  );

const hasLargeGapInsideWindow = (
  gaps: GapInfo[],
  startTimestamp: number,
  endTimestamp: number
) =>
  gaps.some(gap => {
    if (gap.interpolated) return false;
    const gapStart = Date.parse(gap.startTime);
    const gapEnd = gapStart + (gap.durationSec + 1) * 1000;
    return gapStart <= endTimestamp && gapEnd >= startTimestamp;
  });

const buildSegmentInfo = (
  series: ReturnType<typeof buildSegmentSeries>,
  startTimestamp: number,
  endTimestamp: number,
  compromised: boolean,
  label: 'Baseline' | 'Exposure',
  cleaningLog: string[]
) => {
  const segmentName =
    label === 'Baseline' ? 'segmento iniziale' : 'segmento finale';
  const filledSeconds = countFilledSegmentSeconds(series);
  const valid = !compromised && filledSeconds >= SEGMENT_DURATION_SECONDS;
  const missingCompletely = filledSeconds === 0;
  const reason = valid
    ? undefined
    : compromised
      ? `${segmentName} compromesso: gap >3 sec inside`
      : missingCompletely
        ? `${segmentName} assente`
        : `${segmentName} incompleto`;

  if (reason) {
    cleaningLog.push(reason);
  }

  cleaningLog.push(
    `${segmentName} [${toIso(startTimestamp)}-${toIso(endTimestamp)}] ${valid ? 'valid' : 'compromised'}`
  );

  return {
    startTime: toIso(startTimestamp),
    endTime: toIso(endTimestamp),
    durationSec: Math.round((endTimestamp - startTimestamp) / 1000) + 1,
    valid,
    reason,
    compromised,
  };
};

const buildTemporalStats = (
  values: Array<number | undefined>,
  feature: FeatureKey
) => {
  const finiteValues = values.filter((value): value is number =>
    isFiniteNumber(value)
  );
  const stats = {
    mean: round(average(finiteValues)),
    std: round(standardDeviation(finiteValues)),
    min: round(minValue(finiteValues)),
    max: round(maxValue(finiteValues)),
    trend: round(linearTrend(finiteValues), 6),
  };

  if (feature === 'HRV') {
    return {
      ...stats,
      rmssd: round(computeRmssd(finiteValues) ?? 0),
    };
  }

  return stats;
};

const validateSession = (session: ValidationInputSession): ValidationResult => {
  const parsed = parseSession(session);
  const cleaningLog: string[] = [];
  const totalPointsByFeature = FEATURE_KEYS.reduce(
    (accumulator, feature) => ({
      ...accumulator,
      [feature]: parsed.rawCounts[feature],
    }),
    {} as Record<FeatureKey, number>
  );

  const validDataPercentByFeature = FEATURE_KEYS.reduce(
    (accumulator, feature) => ({
      ...accumulator,
      [feature]:
        totalPointsByFeature[feature] === 0
          ? 0
          : round(
              (parsed.samples[feature].length / totalPointsByFeature[feature]) *
                100,
              2
            ),
    }),
    {} as Record<FeatureKey, number>
  );

  const invalidFeature = FEATURE_KEYS.find(
    feature => validDataPercentByFeature[feature] < MIN_VALID_PERCENT
  );

  if (invalidFeature) {
    return {
      included: false,
      excluded: {
        sessionId: parsed.sessionId,
        patientId: parsed.patientId,
        sessionDate: parsed.sessionDate,
        reason: `Feature ${invalidFeature} under ${MIN_VALID_PERCENT}% valid data`,
        validDataPercentByFeature,
      },
    };
  }

  const { aligned, duplicatesRemoved } = buildAlignedSeries(
    parsed.samples,
    cleaningLog
  );

  if (aligned.length === 0) {
    return {
      included: false,
      excluded: {
        sessionId: parsed.sessionId,
        patientId: parsed.patientId,
        sessionDate: parsed.sessionDate,
        reason: 'No aligned points available across HR, HRV, LF and HF',
        validDataPercentByFeature,
      },
    };
  }

  const totalDurationSeconds =
    Math.round(
      (aligned[aligned.length - 1].timestamp - aligned[0].timestamp) / 1000
    ) + 1;

  if (totalDurationSeconds < FULL_SESSION_DURATION_SECONDS) {
    return {
      included: false,
      excluded: {
        sessionId: parsed.sessionId,
        patientId: parsed.patientId,
        sessionDate: parsed.sessionDate,
        reason: `Session shorter than ${FULL_SESSION_DURATION_SECONDS} seconds`,
        validDataPercentByFeature,
      },
    };
  }

  const gapHandling = detectAndHandleGaps(aligned, cleaningLog);
  const firstTimestamp = gapHandling.points[0].timestamp;
  const lastTimestamp =
    gapHandling.points[gapHandling.points.length - 1].timestamp;
  const baselineStart = firstTimestamp;
  const baselineEnd = baselineStart + (SEGMENT_DURATION_SECONDS - 1) * 1000;
  const exposureEnd = lastTimestamp;
  const exposureStart = exposureEnd - (SEGMENT_DURATION_SECONDS - 1) * 1000;

  const baselineSeries = buildSegmentSeries(
    gapHandling.points,
    baselineStart,
    baselineEnd
  );
  const exposureSeries = buildSegmentSeries(
    gapHandling.points,
    exposureStart,
    exposureEnd
  );

  const baselineCompromised = hasLargeGapInsideWindow(
    gapHandling.gaps,
    baselineStart,
    baselineEnd
  );
  const exposureCompromised = hasLargeGapInsideWindow(
    gapHandling.gaps,
    exposureStart,
    exposureEnd
  );

  const baselineInfo = buildSegmentInfo(
    baselineSeries,
    baselineStart,
    baselineEnd,
    baselineCompromised,
    'Baseline',
    cleaningLog
  );
  const exposureInfo = buildSegmentInfo(
    exposureSeries,
    exposureStart,
    exposureEnd,
    exposureCompromised,
    'Exposure',
    cleaningLog
  );

  const baselineHrv = preprocessHrvPath({
    hrValues: baselineSeries.values.HR,
    timestamps: baselineSeries.timestamps,
  });
  const exposureHrv = preprocessHrvPath({
    hrValues: exposureSeries.values.HR,
    timestamps: exposureSeries.timestamps,
  });

  baselineHrv.logs.forEach(entry => cleaningLog.push(`HRV baseline: ${entry}`));
  exposureHrv.logs.forEach(entry => cleaningLog.push(`HRV exposure: ${entry}`));

  if (!baselineHrv.passed || !exposureHrv.passed) {
    const maxPercent = Math.max(
      baselineHrv.correctedBeatPercent,
      exposureHrv.correctedBeatPercent
    );
    return {
      included: false,
      excluded: {
        sessionId: parsed.sessionId,
        patientId: parsed.patientId,
        sessionDate: parsed.sessionDate,
        reason: `HRV artifact correction exceeded 5% threshold (max ${round(maxPercent, 2)}% corrected beats, Kubios-style)`,
        validDataPercentByFeature,
      },
    };
  }

  const hrvPreprocessing = {
    preprocessingVersion: PREPROCESSING_VERSION,
    nnSource: NN_SOURCE,
    artifactMethod: ARTIFACT_METHOD,
    correctedBeatPercentBaseline: baselineHrv.correctedBeatPercent,
    correctedBeatPercentExposure: exposureHrv.correctedBeatPercent,
    correctedBeatPercentPassed:
      baselineHrv.correctedBeatPercentPassed &&
      exposureHrv.correctedBeatPercentPassed,
    detrendMethod: DETREND_METHOD,
    detrendLambda: SMOOTH_PRIORS_LAMBDA,
    resampleHz: RESAMPLE_HZ,
    spectralPathPrimary: SPECTRAL_PATH_PRIMARY,
    rmssdSource: RMSSD_SOURCE,
  };

  const metrics = FEATURE_KEYS.reduce(
    (accumulator, feature) => {
      const baselineStats = buildTemporalStats(
        baselineSeries.values[feature],
        feature
      );
      const exposureStats = buildTemporalStats(
        exposureSeries.values[feature],
        feature
      );

      const baselineWithRmssd =
        feature === 'HRV'
          ? { ...baselineStats, rmssd: round(baselineHrv.rmssd, 2) }
          : baselineStats;
      const exposureWithRmssd =
        feature === 'HRV'
          ? { ...exposureStats, rmssd: round(exposureHrv.rmssd, 2) }
          : exposureStats;

      return {
        ...accumulator,
        [feature]: {
          baseline: baselineWithRmssd,
          exposure: exposureWithRmssd,
          delta: round(exposureStats.mean - baselineStats.mean),
        },
      };
    },
    {} as ValidatedSessionAnalysis['metrics']
  );

  const overallValidPercent = round(
    average(FEATURE_KEYS.map(feature => validDataPercentByFeature[feature])),
    2
  );

  let penalties = 0;
  if (baselineCompromised) penalties += 10;
  if (exposureCompromised) penalties += 10;

  FEATURE_KEYS.forEach(feature => {
    if (validDataPercentByFeature[feature] < QUALITY_WARNING_THRESHOLD) {
      penalties += 5;
    }
  });

  const qualityScore = clampNumber(100 - penalties, 0, 100);
  const qualityLevel =
    qualityScore > 90 && baselineInfo.valid && exposureInfo.valid
      ? 'GOOD'
      : qualityScore >= 70 && (baselineInfo.valid || exposureInfo.valid)
        ? 'FAIR'
        : 'POOR';

  cleaningLog.push(`Quality score: ${qualityScore}% - ${qualityLevel}`);

  return {
    included: true,
    validation: {
      sessionId: parsed.sessionId,
      patientId: parsed.patientId,
      sessionDate: parsed.sessionDate,
      quality: {
        score: qualityScore,
        level: qualityLevel,
        validDataPercent: overallValidPercent,
        validDataPercentByFeature,
        gaps: gapHandling.gaps,
        duplicatesRemoved,
      },
      segments: {
        baseline: baselineInfo,
        exposure: exposureInfo,
      },
      metrics,
      cleaningLog,
      series: {
        baseline: baselineSeries,
        exposure: exposureSeries,
      },
      preprocessingCounts: {
        originalPoints: Math.min(
          ...FEATURE_KEYS.map(feature => totalPointsByFeature[feature])
        ),
        afterAlignment: aligned.length,
        afterDedup: aligned.length,
        afterGapHandling: gapHandling.points.length,
        interpolatedPoints: gapHandling.interpolatedPoints,
      },
      hrvPreprocessing,
      hrvPreprocessSegments: {
        baseline: baselineHrv,
        exposure: exposureHrv,
      },
    },
  };
};

export const zScoreNormalize = (values: number[]) => {
  const mean = average(values);
  const std = standardDeviation(values);
  if (std === 0) return values.map(() => 0);
  return values.map(value => (value - mean) / std);
};

const biquadLowPass = (
  values: number[],
  cutoffHz: number,
  sampleRate: number
) => {
  if (values.length === 0) return [];
  const nyquist = sampleRate / 2;
  const effectiveCutoff = Math.min(cutoffHz, nyquist - 0.001);
  const omega = (2 * Math.PI * effectiveCutoff) / sampleRate;
  const sinOmega = Math.sin(omega);
  const cosOmega = Math.cos(omega);
  const q = Math.SQRT1_2;
  const alpha = sinOmega / (2 * q);

  let b0 = (1 - cosOmega) / 2;
  let b1 = 1 - cosOmega;
  let b2 = (1 - cosOmega) / 2;
  const a0 = 1 + alpha;
  let a1 = -2 * cosOmega;
  let a2 = 1 - alpha;

  b0 /= a0;
  b1 /= a0;
  b2 /= a0;
  a1 /= a0;
  a2 /= a0;

  const output = new Array<number>(values.length).fill(0);
  let x1 = 0;
  let x2 = 0;
  let y1 = 0;
  let y2 = 0;

  for (let index = 0; index < values.length; index += 1) {
    const x0 = values[index];
    const y0 = b0 * x0 + b1 * x1 + b2 * x2 - a1 * y1 - a2 * y2;
    output[index] = y0;
    x2 = x1;
    x1 = x0;
    y2 = y1;
    y1 = y0;
  }

  return output;
};

export const forwardBackwardLowPass = (
  values: number[],
  cutoffHz: number = LOW_PASS_CUTOFF_HZ,
  sampleRate: number = SAMPLING_RATE
) => {
  const forward = biquadLowPass(values, cutoffHz, sampleRate);
  const backward = biquadLowPass([...forward].reverse(), cutoffHz, sampleRate);
  return backward.reverse();
};

const integrateBandPower = (
  frequencies: number[],
  psd: number[],
  minHz: number,
  maxHz: number
) => {
  if (frequencies.length <= 1) return 0;
  const deltaF = frequencies[1] - frequencies[0];
  let power = 0;
  for (let index = 0; index < frequencies.length; index += 1) {
    const frequency = frequencies[index];
    if (frequency >= minHz && frequency < maxHz) {
      power += psd[index] * deltaF;
    }
  }
  return power;
};

const computeBandMetrics = (
  psd: PsdSeries,
  key: 'psd_lf' | 'psd_hf'
): SpectralBandMetrics => {
  const values = psd[key];
  const frequencies = psd.frequencies;
  const powerLf = integrateBandPower(frequencies, values, 0.04, 0.15);
  const powerHf = integrateBandPower(frequencies, values, 0.15, 0.4);

  let maxIndex = 0;
  for (let index = 1; index < values.length; index += 1) {
    if (values[index] > values[maxIndex]) {
      maxIndex = index;
    }
  }

  const totalPower = values.reduce((sum, value) => sum + value, 0);
  const centroid =
    totalPower === 0
      ? 0
      : frequencies.reduce(
          (sum, frequency, index) => sum + frequency * values[index],
          0
        ) / totalPower;

  return {
    power: round(powerLf + powerHf, 6),
    peakFreq: round(frequencies[maxIndex] ?? 0, 6),
    peakAmplitude: round(values[maxIndex] ?? 0, 6),
    centroid: round(centroid, 6),
    normalized: round(powerLf / (powerLf + powerHf || 1), 6),
  };
};

const buildSpectralResult = (
  validation: ValidatedSessionAnalysis
): SpectralSessionResult => {
  const finiteSegmentValues = (values: Array<number | undefined>) =>
    values.filter((value): value is number => isFiniteNumber(value));

  const baselineNormalized = FEATURE_KEYS.reduce(
    (accumulator, feature) => ({
      ...accumulator,
      [feature]: forwardBackwardLowPass(
        zScoreNormalize(
          finiteSegmentValues(validation.series.baseline.values[feature])
        )
      ),
    }),
    {} as Record<FeatureKey, number[]>
  );
  const exposureNormalized = FEATURE_KEYS.reduce(
    (accumulator, feature) => ({
      ...accumulator,
      [feature]: forwardBackwardLowPass(
        zScoreNormalize(
          finiteSegmentValues(validation.series.exposure.values[feature])
        )
      ),
    }),
    {} as Record<FeatureKey, number[]>
  );

  const baselineNnPsd = computeWelchPsd(
    validation.hrvPreprocessSegments.baseline.resampled4Hz,
    RESAMPLE_HZ
  );
  const exposureNnPsd = computeWelchPsd(
    validation.hrvPreprocessSegments.exposure.resampled4Hz,
    RESAMPLE_HZ
  );

  const baselinePsd: PsdSeries = {
    frequencies: baselineNnPsd.frequencies,
    psd_lf: baselineNnPsd.psd_lf,
    psd_hf: baselineNnPsd.psd_hf,
  };
  const exposurePsd: PsdSeries = {
    frequencies: exposureNnPsd.frequencies,
    psd_lf: exposureNnPsd.psd_lf,
    psd_hf: exposureNnPsd.psd_hf,
  };

  const baselineDeviceLfPsd = computeWelchPsd(baselineNormalized.LF);
  const baselineDeviceHfPsd = computeWelchPsd(baselineNormalized.HF);
  const exposureDeviceLfPsd = computeWelchPsd(exposureNormalized.LF);
  const exposureDeviceHfPsd = computeWelchPsd(exposureNormalized.HF);
  const deviceBaselineLfPower = integrateBandPower(
    baselineDeviceLfPsd.frequencies,
    baselineDeviceLfPsd.psd_lf,
    0.04,
    0.15
  );
  const deviceBaselineHfPower = integrateBandPower(
    baselineDeviceHfPsd.frequencies,
    baselineDeviceHfPsd.psd_hf,
    0.15,
    0.4
  );
  const deviceExposureLfPower = integrateBandPower(
    exposureDeviceLfPsd.frequencies,
    exposureDeviceLfPsd.psd_lf,
    0.04,
    0.15
  );
  const deviceExposureHfPower = integrateBandPower(
    exposureDeviceHfPsd.frequencies,
    exposureDeviceHfPsd.psd_hf,
    0.15,
    0.4
  );

  const lfBaseline = computeBandMetrics(baselinePsd, 'psd_lf');
  const lfExposure = computeBandMetrics(exposurePsd, 'psd_lf');
  const hfBaseline = computeBandMetrics(baselinePsd, 'psd_hf');
  const hfExposure = computeBandMetrics(exposurePsd, 'psd_hf');

  const baselineLfPower = integrateBandPower(
    baselinePsd.frequencies,
    baselinePsd.psd_lf,
    0.04,
    0.15
  );
  const baselineHfPower = integrateBandPower(
    baselinePsd.frequencies,
    baselinePsd.psd_hf,
    0.15,
    0.4
  );
  const exposureLfPower = integrateBandPower(
    exposurePsd.frequencies,
    exposurePsd.psd_lf,
    0.04,
    0.15
  );
  const exposureHfPower = integrateBandPower(
    exposurePsd.frequencies,
    exposurePsd.psd_hf,
    0.15,
    0.4
  );

  return {
    sessionId: validation.sessionId,
    temporalMetrics: validation.metrics,
    spectralMetrics: {
      HR: {
        baseline: {
          mean: validation.metrics.HR.baseline.mean,
          std: validation.metrics.HR.baseline.std,
        },
        exposure: {
          mean: validation.metrics.HR.exposure.mean,
          std: validation.metrics.HR.exposure.std,
        },
        delta: validation.metrics.HR.delta,
        trend: validation.metrics.HR.exposure.trend,
      },
      HRV: {
        baseline: {
          mean: validation.metrics.HRV.baseline.mean,
          std: validation.metrics.HRV.baseline.std,
          rmssd: validation.metrics.HRV.baseline.rmssd,
        },
        exposure: {
          mean: validation.metrics.HRV.exposure.mean,
          std: validation.metrics.HRV.exposure.std,
          rmssd: validation.metrics.HRV.exposure.rmssd,
        },
        delta: validation.metrics.HRV.delta,
        trend: validation.metrics.HRV.exposure.trend,
      },
      LF: {
        baseline: lfBaseline,
        exposure: lfExposure,
        delta: round(lfExposure.power - lfBaseline.power, 6),
      },
      HF: {
        baseline: hfBaseline,
        exposure: hfExposure,
        delta: round(hfExposure.power - hfBaseline.power, 6),
      },
      LFHF_ratio: {
        baseline: round(baselineLfPower / (baselineHfPower || 1), 6),
        exposure: round(exposureLfPower / (exposureHfPower || 1), 6),
        delta: round(
          exposureLfPower / (exposureHfPower || 1) -
            baselineLfPower / (baselineHfPower || 1),
          6
        ),
      },
    },
    psd: {
      baseline: baselinePsd,
      exposure: exposurePsd,
    },
    preprocessingSummary: {
      ...validation.preprocessingCounts,
      ...validation.hrvPreprocessing,
      filterApplied:
        'Primary: NN tachogram 4 Hz (smooth priors detrend); Device comparison: Butterworth 0.5 Hz forward-backward on LF/HF @ 1 Hz',
      fftMethod: `Welch on ${RESAMPLE_HZ} Hz NN-reconstructed tachogram (primary); device LF/HF @ 1 Hz retained for comparison (LF/HF ratio device baseline ${round(deviceBaselineLfPower / (deviceBaselineHfPower || 1), 4)}, exposure ${round(deviceExposureLfPower / (deviceExposureHfPower || 1), 4)})`,
      spectralPathDevice: SPECTRAL_PATH_DEVICE,
    },
    outlier: false,
  };
};

const computeOutliers = (
  validations: ValidatedSessionAnalysis[],
  results: SpectralSessionResult[]
) => {
  const sharedOutliers = mahalanobisOutliers(validations);
  const outlierBySessionId = new Map(
    sharedOutliers.outlierSessions.map(outlier => [
      outlier.sessionId,
      outlier.isOutlier,
    ])
  );

  return results.map(result => ({
    ...result,
    outlier: outlierBySessionId.get(result.sessionId) ?? false,
  }));
};

const aggregateHrTimeline = (
  validations: ValidatedSessionAnalysis[]
): SpectralGroupSummary['aggregatedHrTimeline'] => {
  const output: SpectralGroupSummary['aggregatedHrTimeline'] = [];

  for (let second = 0; second < SEGMENT_DURATION_SECONDS; second += 1) {
    const baselineValues = validations
      .map(validation => validation.series.baseline.values.HR[second])
      .filter(isFiniteNumber);
    const exposureValues = validations
      .map(validation => validation.series.exposure.values.HR[second])
      .filter(isFiniteNumber);

    output.push({
      second,
      mean: round(average(baselineValues)),
      std: round(standardDeviation(baselineValues)),
      segment: 'baseline',
    });
    output.push({
      second: second + SEGMENT_DURATION_SECONDS,
      mean: round(average(exposureValues)),
      std: round(standardDeviation(exposureValues)),
      segment: 'exposure',
    });
  }

  return output;
};

const aggregatePsd = (
  results: SpectralSessionResult[],
  segment: 'baseline' | 'exposure'
) => {
  if (results.length === 0) {
    return {
      frequencies: [],
      psd_lf: [],
      psd_hf: [],
    };
  }

  const frequencies = results[0].psd[segment].frequencies;
  const psdLf = new Array(frequencies.length).fill(0);
  const psdHf = new Array(frequencies.length).fill(0);

  results.forEach(result => {
    result.psd[segment].psd_lf.forEach((value, index) => {
      psdLf[index] += value;
    });
    result.psd[segment].psd_hf.forEach((value, index) => {
      psdHf[index] += value;
    });
  });

  return {
    frequencies,
    psd_lf: psdLf.map(value => round(value / results.length, 6)),
    psd_hf: psdHf.map(value => round(value / results.length, 6)),
  };
};

const shapiroLikePValue = (values: number[]) => {
  if (values.length < 3) return 1;

  const mean = average(values);
  const std = standardDeviation(values);
  if (std === 0) return 1;

  const normalized = values
    .map(value => (value - mean) / std)
    .sort((left, right) => left - right);
  const n = normalized.length;
  const expected = normalized.map((_, index) =>
    inverseNormalCdf((index + 1 - 0.375) / (n + 0.25))
  );
  const expectedNorm = Math.sqrt(
    expected.reduce((sum, value) => sum + value * value, 0)
  );
  const weights = expected.map(value => value / (expectedNorm || 1));
  const numerator = normalized.reduce(
    (sum, value, index) => sum + weights[index] * value,
    0
  );
  const denominator = normalized.reduce((sum, value) => sum + value * value, 0);
  const w = denominator === 0 ? 1 : (numerator * numerator) / denominator;
  const transformed = Math.max(1e-8, 1 - w);
  const statistic = Math.sqrt(n) * transformed;
  const pValue = 2 * (1 - normalCdf(Math.abs(statistic)));
  return clampNumber(pValue, 0, 1);
};

const invert2x2Matrix = (matrix: [[number, number], [number, number]]) => {
  const determinant = matrix[0][0] * matrix[1][1] - matrix[0][1] * matrix[1][0];
  if (Math.abs(determinant) < 1e-8) {
    return [
      [1, 0],
      [0, 1],
    ] as [[number, number], [number, number]];
  }

  return [
    [matrix[1][1] / determinant, -matrix[0][1] / determinant],
    [-matrix[1][0] / determinant, matrix[0][0] / determinant],
  ] as [[number, number], [number, number]];
};

const mahalanobisOutliers = (
  validations: ValidatedSessionAnalysis[]
): StatisticalValidationEntry['outlierDetection'] => {
  if (validations.length === 0) {
    return { outlierSessions: [], n_outliers: 0 };
  }

  const points = validations.map(validation => ({
    sessionId: validation.sessionId,
    hr: validation.metrics.HR.delta,
    hrv: validation.metrics.HRV.delta,
  }));
  const meanHr = average(points.map(point => point.hr));
  const meanHrv = average(points.map(point => point.hrv));
  const covarianceXX =
    average(points.map(point => (point.hr - meanHr) ** 2)) || 1e-6;
  const covarianceYY =
    average(points.map(point => (point.hrv - meanHrv) ** 2)) || 1e-6;
  const covarianceXY =
    average(points.map(point => (point.hr - meanHr) * (point.hrv - meanHrv))) ||
    0;
  const inverseCovariance = invert2x2Matrix([
    [covarianceXX, covarianceXY],
    [covarianceXY, covarianceYY],
  ]);

  const outlierSessions = points.map(point => {
    const dx = point.hr - meanHr;
    const dy = point.hrv - meanHrv;
    const distanceSquared =
      dx * (inverseCovariance[0][0] * dx + inverseCovariance[0][1] * dy) +
      dy * (inverseCovariance[1][0] * dx + inverseCovariance[1][1] * dy);
    const distance = Math.sqrt(Math.max(distanceSquared, 0));

    return {
      sessionId: point.sessionId,
      mahalanobis_distance: round(distance, 6),
      isOutlier: distance > 3,
    };
  });

  return {
    outlierSessions,
    n_outliers: outlierSessions.filter(item => item.isOutlier).length,
  };
};

const buildStatisticalValidation = (
  validations: ValidatedSessionAnalysis[],
  results: SpectralSessionResult[]
): StatisticalValidationEntry[] => {
  if (validations.length < 3) return [];

  const sharedOutliers = mahalanobisOutliers(validations);

  return FEATURE_KEYS.map(feature => {
    const baselineValues =
      feature === 'LF'
        ? results.map(result => result.spectralMetrics.LF.baseline.power)
        : feature === 'HF'
          ? results.map(result => result.spectralMetrics.HF.baseline.power)
          : validations.map(
              validation => validation.metrics[feature].baseline.mean
            );
    const exposureValues =
      feature === 'LF'
        ? results.map(result => result.spectralMetrics.LF.exposure.power)
        : feature === 'HF'
          ? results.map(result => result.spectralMetrics.HF.exposure.power)
          : validations.map(
              validation => validation.metrics[feature].exposure.mean
            );
    const deltas = exposureValues.map(
      (value, index) => value - baselineValues[index]
    );
    const baselineMean = average(baselineValues);
    const exposureMean = average(exposureValues);
    const meanDelta = average(deltas);
    const stdDelta = standardDeviation(deltas);
    const sampleSize = deltas.length;
    const standardError = stdDelta / Math.sqrt(sampleSize || 1);
    const degreesOfFreedom = sampleSize - 1;
    const tStatistic = standardError === 0 ? 0 : meanDelta / standardError;
    const pValue =
      degreesOfFreedom > 0
        ? clampNumber(
            2 * (1 - studentTCdf(Math.abs(tStatistic), degreesOfFreedom)),
            0,
            1
          )
        : 1;
    const pooledStd = Math.sqrt(
      (standardDeviation(baselineValues) ** 2 +
        standardDeviation(exposureValues) ** 2) /
        2
    );
    const cohensD = pooledStd === 0 ? 0 : meanDelta / pooledStd;
    const tCritical =
      degreesOfFreedom > 0 ? inverseStudentT(0.975, degreesOfFreedom) : 0;
    const ci95 = {
      lower: round(meanDelta - tCritical * standardError, 6),
      upper: round(meanDelta + tCritical * standardError, 6),
    };

    const baselineShapiro = shapiroLikePValue(baselineValues);
    const exposureShapiro = shapiroLikePValue(exposureValues);
    const deltaShapiro = shapiroLikePValue(deltas);
    const baselineNormal = baselineShapiro > 0.05;
    const exposureNormal = exposureShapiro > 0.05;
    const deltaNormal = deltaShapiro > 0.05;

    return {
      feature,
      pairedTest: {
        baseline_mean: round(baselineMean, 6),
        exposure_mean: round(exposureMean, 6),
        delta: round(meanDelta, 6),
        t_statistic: round(tStatistic, 6),
        p_value: round(pValue, 6),
        cohens_d: round(cohensD, 6),
        ci_95: ci95,
        significant: pValue < 0.05,
        effectSize: effectSizeLabel(cohensD),
      },
      normality: {
        baseline_shapiro_p: round(baselineShapiro, 6),
        exposure_shapiro_p: round(exposureShapiro, 6),
        delta_shapiro_p: round(deltaShapiro, 6),
        baseline_normal: baselineNormal,
        exposure_normal: exposureNormal,
        delta_normal: deltaNormal,
        warning:
          baselineNormal && exposureNormal && deltaNormal
            ? undefined
            : 'Consider non-parametric test',
      },
      outlierDetection: sharedOutliers,
    };
  });
};

export const buildGroupSummary = (
  validations: ValidatedSessionAnalysis[],
  results: SpectralSessionResult[],
  excludedSessions: ExcludedSessionReason[]
): SpectralGroupSummary => {
  const hrDeltas = results.map(result => result.spectralMetrics.HR.delta);
  const lfHfDeltas = results.map(
    result => result.spectralMetrics.LFHF_ratio.delta
  );

  return {
    totalSessions: validations.length + excludedSessions.length,
    includedSessions: validations.length,
    excludedSessions: excludedSessions.length,
    goodSessions: validations.filter(
      validation => validation.quality.level === 'GOOD'
    ).length,
    fairSessions: validations.filter(
      validation => validation.quality.level === 'FAIR'
    ).length,
    poorSessions: validations.filter(
      validation => validation.quality.level === 'POOR'
    ).length,
    outlierCount: results.filter(result => result.outlier).length,
    averageHrDelta: round(average(hrDeltas)),
    hrDeltaStd: round(standardDeviation(hrDeltas)),
    averageLfHfDelta: round(average(lfHfDeltas), 6),
    lfHfDeltaStd: round(standardDeviation(lfHfDeltas), 6),
    aggregatedHrTimeline: aggregateHrTimeline(validations),
    aggregatedPsd: {
      baseline: aggregatePsd(results, 'baseline'),
      exposure: aggregatePsd(results, 'exposure'),
    },
    statisticalValidation: buildStatisticalValidation(validations, results),
  };
};

export const runSpectralAnalysis = (sessions: ValidationInputSession[]) => {
  const validations: ValidatedSessionAnalysis[] = [];
  const excludedSessions: ExcludedSessionReason[] = [];

  sessions.forEach(session => {
    const result = validateSession(session);
    if (result.included) {
      validations.push(result.validation);
    } else {
      excludedSessions.push(result.excluded);
    }
  });

  const spectralResults = computeOutliers(
    validations,
    validations.map(buildSpectralResult)
  );
  const groupSummary = buildGroupSummary(
    validations,
    spectralResults,
    excludedSessions
  );

  return {
    validations,
    spectralResults,
    excludedSessions,
    groupSummary,
  };
};

export const buildVerificationPayload = (
  snapshot: SpectralAnalysisSnapshot
) => ({
  analysisId: snapshot.analysisId,
  selectedSessions: snapshot.selectedSessions.length,
  includedSessions: snapshot.includedSessions.length,
  excludedSessions: snapshot.excludedSessions.length,
  averageHrDelta: snapshot.groupSummary.averageHrDelta,
  averageLfHfDelta: snapshot.groupSummary.averageLfHfDelta,
  outlierCount: snapshot.groupSummary.outlierCount,
  sessions: snapshot.includedSessions.map(session => ({
    sessionId: session.validation.sessionId,
    quality: session.validation.quality,
    baselineValid: session.validation.segments.baseline.valid,
    exposureValid: session.validation.segments.exposure.valid,
    hrDelta: session.spectral.spectralMetrics.HR.delta,
    lfHfDelta: session.spectral.spectralMetrics.LFHF_ratio.delta,
    preprocessingSummary: session.spectral.preprocessingSummary,
  })),
});
